import React, { useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY ANALYSIS PANEL — ZONE B2
   ----------------------------------------------------------------------------
   OBJECTIF
   - bac à sable de composition de package
   - réception des cartes glissées depuis B1
   - retour des cartes vers leur cellule d’origine
   - bouton de création de package (placeholder)
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

const DEFAULT_SUMMARY = {
  dominantTrade: "Génie civil",
  dominantNature: "Matériaux",
  supplyProfile: "Mixte à dominante locale",
  fieldPressure: "Élevée",
  keySensitivity: "Interfaces process / électricité",
};

const DEFAULT_ALERTS = [
  { tone: "orange", text: "Équipements process critiques à approvisionnement import." },
  { tone: "yellow", text: "Charge matériaux élevée sur réseaux et génie civil." },
  { tone: "grey", text: "Sous-traitance significative sur la pose réseaux." },
  { tone: "green", text: "Main d’œuvre électricité encore contenue à ce stade." },
];

const DEFAULT_SELECTED_ITEM = {
  title: "Pompes",
  trade: "Process",
  nature: "Équipements",
  quantity: "12 u",
  supply: "Import",
  intensity: "Critique",
};

const PanelBlock = ({ title, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 2 }}>
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.84rem",
        lineHeight: 1.12,
        color: theme?.colors?.text?.caneva || "#c24c2c",
        fontWeight: 600,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.74rem",
        lineHeight: 1.2,
        color: T.soft,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.76rem",
        lineHeight: 1.2,
        color: valueColor,
        fontWeight: 600,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  </div>
);

const SignalRow = ({ text, tone = "grey" }) => {
  const color =
    tone === "green"
      ? T.greenText
      : tone === "yellow"
      ? T.yellowText
      : tone === "orange"
      ? T.orangeText
      : tone === "red"
      ? T.redText
      : T.greyText;

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <div
        style={{
          width: 5,
          height: 5,
          borderRadius: 999,
          background: color,
          marginTop: 6,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.72rem",
          lineHeight: 1.35,
          color: T.soft,
        }}
      >
        {text}
      </div>
    </div>
  );
};

/* ============================================================================
   HELPERS — SANDBOX
============================================================================ */

const addItemBackToMatrix = (item, setMatrixData) => {
  const targetColumnKey = item.columnKey || item.originColumnKey;
  const targetTradeKey = item.tradeKey || item.originTradeKey;

  if (!targetColumnKey || !targetTradeKey) return;

  setMatrixData((prev) => {
    const next = { ...prev };
    const currentColumn = { ...(next[targetColumnKey] || {}) };
    const currentItems = [...(currentColumn[targetTradeKey] || [])];

    const exists = currentItems.some((existing) => existing.id === item.id);
    if (exists) return prev;

    currentColumn[targetTradeKey] = [
      ...currentItems,
      {
        ...item,
        columnKey: targetColumnKey,
        tradeKey: targetTradeKey,
        label: item.title || item.label,
      },
    ];

    next[targetColumnKey] = currentColumn;
    return next;
  });
};

/* ============================================================================
   SANDBOX
============================================================================ */

const SandboxChip = ({ item, onRemove }) => (
  <div
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData("application/json", JSON.stringify(item));
      e.dataTransfer.setData("source", "B2");
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "2px 6px",
      border: `1px solid ${T.border}`,
      borderRadius: 3,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.70rem",
      lineHeight: 1.25,
      color: T.text,
      background: "#ffffff",
      cursor: "grab",
    }}
  >
    <span>{item.label || item.title}</span>

    <span
      onClick={(e) => {
        e.stopPropagation();
        onRemove(item);
      }}
      style={{
        color: T.soft,
        cursor: "pointer",
        fontWeight: 600,
        lineHeight: 1,
      }}
      title="Retirer du sandbox"
    >
      ×
    </span>
  </div>
);

const SandboxContainer = ({ items, setItems, setMatrixData }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);

    const source = e.dataTransfer.getData("source");
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;

    const droppedItem = JSON.parse(raw);

    if (source === "B1") {
      setItems((prev) => {
        const exists = prev.some((item) => item.id === droppedItem.id);
        if (exists) return prev;
        return [...prev, droppedItem];
      });

      setMatrixData((prev) => {
        const columnKey = droppedItem.columnKey;
        const tradeKey = droppedItem.tradeKey;

        if (!columnKey || !tradeKey) return prev;

        const next = { ...prev };
        const currentColumn = { ...(next[columnKey] || {}) };
        const filteredItems = (currentColumn[tradeKey] || []).filter(
          (item) => item.id !== droppedItem.id
        );

        currentColumn[tradeKey] = filteredItems;
        next[columnKey] = currentColumn;

        return next;
      });
    }
  };

  const handleRemoveFromSandbox = (itemToReturn) => {
    setItems((prev) => prev.filter((item) => item.id !== itemToReturn.id));
    addItemBackToMatrix(itemToReturn, setMatrixData);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={handleDrop}
      style={{
        width: "100%",
        minHeight: 90,
        border: `1px dashed ${isOver ? T.blueText : T.border}`,
        borderRadius: 3,
        padding: 8,
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        background: isOver ? "rgba(30,80,102,0.04)" : "#fff",
        boxSizing: "border-box",
      }}
    >
      {items.length === 0 && (
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "0.72rem",
            lineHeight: 1.35,
            color: T.verySoft,
          }}
        >
          Glisser des éléments ici
        </div>
      )}

      {items.map((item) => (
        <SandboxChip
          key={item.id}
          item={item}
          onRemove={handleRemoveFromSandbox}
        />
      ))}
    </div>
  );
};

const ActivityAnalysisPanel = ({
  summary = DEFAULT_SUMMARY,
  alerts = DEFAULT_ALERTS,
  selectedItem = DEFAULT_SELECTED_ITEM,
  sandboxItems = [],
  setSandboxItems,
  setMatrixData,
}) => {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 22,
      }}
    >
      {/* SANDBOX */}
      <PanelBlock title="Sandbox">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SandboxContainer
            items={sandboxItems}
            setItems={setSandboxItems}
            setMatrixData={setMatrixData}
          />

          <button
            disabled={sandboxItems.length === 0}
            style={{
              height: 30,
              border: `1px solid ${sandboxItems.length ? T.blueText : T.border}`,
              background: sandboxItems.length ? "rgba(30,80,102,0.06)" : "#fff",
              color: sandboxItems.length ? T.blueText : T.soft,
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.74rem",
              fontWeight: 600,
              borderRadius: 3,
              cursor: sandboxItems.length ? "pointer" : "default",
            }}
          >
            Créer package
          </button>
        </div>
      </PanelBlock>

      {/* RESTE INCHANGÉ */}
      <PanelBlock title="Synthèse opérationnelle">
        <KeyValueRow label="Lot dominant" value={summary.dominantTrade} valueColor={T.blueText} />
        <KeyValueRow label="Nature dominante" value={summary.dominantNature} valueColor={T.greenText} />
      </PanelBlock>

      <PanelBlock title="Points d’attention">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {alerts.map((a, i) => (
            <SignalRow key={`${a.text}-${i}`} {...a} />
          ))}
        </div>
      </PanelBlock>

      {selectedItem && (
        <PanelBlock title="Élément sélectionné">
          <KeyValueRow label="Lot métier" value={selectedItem.trade} />
        </PanelBlock>
      )}
    </div>
  );
};

export default ActivityAnalysisPanel;